import merge from "lodash/merge";
import { useEffect, useMemo, useState } from "react";
import {
    ABANDONED_COLOR,
    COMPLETE_APPROVED_COLOR,
    COMPLETE_DENIED_COLOR,
    PROCEED_FAIL_COLOR,
    PROCEED_PASS_COLOR
} from "@constants/colors";
import { DEFAULT_HIGHCHARTS_STACKED_BAR_CHART_OPTIONS } from "@constants/highcharts";
import { WorkflowReportRpServiceConfiguration, WorkflowReportStepByStepResponse } from "@idw-types/data/reports";
import { HighchartsChartContainer } from "../../../../common/highcharts-chart-container/highcharts-chart-container";
import { WORKFLOW_STEP_BY_STEP_ABANDONED, WORKFLOW_STEP_BY_STEP_COMPLETE_APPROVED, WORKFLOW_STEP_BY_STEP_COMPLETE_DENIED, WORKFLOW_STEP_BY_STEP_PROCEED_FAIL, WORKFLOW_STEP_BY_STEP_PROCEED_PASS } from "@constants/workflow-report";

type WorkflowReportStepByStepChartProps = {
    stepByStepResponse: WorkflowReportStepByStepResponse;
    rpServices: Array<WorkflowReportRpServiceConfiguration>;
};

export const WorkflowReportStepByStepChart = (props: WorkflowReportStepByStepChartProps) => {
    const { stepByStepResponse, rpServices } = props;

    const data = useMemo(() => {
        const series: Array<{ name: string; color: string; data: Array<number> }> = [
            {
                name: WORKFLOW_STEP_BY_STEP_ABANDONED,
                color: ABANDONED_COLOR,
                data: []
            },
            {
                name: WORKFLOW_STEP_BY_STEP_PROCEED_FAIL,
                color: PROCEED_FAIL_COLOR,
                data: []
            },
            {
                name: WORKFLOW_STEP_BY_STEP_PROCEED_PASS,
                color: PROCEED_PASS_COLOR,
                data: []
            },
            {
                name: WORKFLOW_STEP_BY_STEP_COMPLETE_DENIED,
                color: COMPLETE_DENIED_COLOR,
                data: []
            },
            {
                name: WORKFLOW_STEP_BY_STEP_COMPLETE_APPROVED,
                color: COMPLETE_APPROVED_COLOR,
                data: []
            }
        ];
        const categories: Array<string> = [];
        stepByStepResponse.forEach(serviceData => {
            const name = rpServices.find(rpService => rpService.id === serviceData.rpServiceID)?.serviceName || `${serviceData.rpServiceID}`;
            // add each service as a category
            categories.push(name);

            // push to each data array
            const completeApprovedSeries = series.find(serie => serie.name === WORKFLOW_STEP_BY_STEP_COMPLETE_APPROVED);
            if (completeApprovedSeries) {
                completeApprovedSeries.data.push(serviceData.completeApproved);
            }
            const completeDeniedSeries = series.find(serie => serie.name === WORKFLOW_STEP_BY_STEP_COMPLETE_DENIED);
            if (completeDeniedSeries) {
                completeDeniedSeries.data.push(serviceData.completeDenied);
            }
            const proceedPassSeries = series.find(serie => serie.name === WORKFLOW_STEP_BY_STEP_PROCEED_PASS);
            if (proceedPassSeries) {
                proceedPassSeries.data.push(serviceData.proceedPass);
            }
            const proceedFailSeries = series.find(serie => serie.name === WORKFLOW_STEP_BY_STEP_PROCEED_FAIL);
            if (proceedFailSeries) {
                proceedFailSeries.data.push(serviceData.proceedFail);
            }
            const abandonedSeries = series.find(serie => serie.name === WORKFLOW_STEP_BY_STEP_ABANDONED);
            if (abandonedSeries) {
                abandonedSeries.data.push(serviceData.abandoned);
            }
        });
        return { series, categories };
    }, [stepByStepResponse]);

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>(merge({}, DEFAULT_HIGHCHARTS_STACKED_BAR_CHART_OPTIONS, {
        chart: {
            height: "400"
        },
        // plotOptions: {
        //     bar: {
        //         cursor: "pointer",
        //     }
        // },
        xAxis: {
            categories: data.categories
        },
        yAxis: {
            labels: {
                format: '{value}%'
            }
        },
        series: data.series,
        legend: ""
    }));

    useEffect(() => {
        const newChartOptions: Highcharts.Options = merge({}, chartOptions, {
            xAxis: {
                categories: data.categories
            },
            series: data.series
        });
        setChartOptions(newChartOptions);
    }, [data]);

    return <HighchartsChartContainer options={chartOptions} />;
};